/**
 * COP Dashboard - Alerts Tab Component (Enhanced)
 * Displays SOS messages and emergency alerts with pulsing animations and visual urgency
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  List,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Badge,
  useTheme,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import {
  Search as SearchIcon,
  ReportProblem as WarningIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Refresh as RefreshIcon,
  NotificationsActive as AlertActiveIcon,
  Traffic as TrafficIcon,
  Thunderstorm as WeatherIcon,
  Route as MissionIcon,
  PlaylistAddCheck as AckIcon,
  BookmarkAdd as PromoteIcon
} from '@mui/icons-material';
import { copAlertsService, SOSMessage } from '../services/copAlertsService';
import { getDeviceColor } from '../utils/deviceColors';
import type { TrafficIncident, WeatherAlert } from '@signals/app-shell/services/areaIntelService';
import {
  MissionEvent,
  MissionRoute,
  isThreatEvent,
  missionEventLabel,
  missionSeverityColor,
} from '../services/missionEventsService';

// Keyframe animations
const pulseAnimation = `
  @keyframes pulse {
    0% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.05); opacity: 0.8; }
    100% { transform: scale(1); opacity: 1; }
  }
  @keyframes glow {
    0% { box-shadow: 0 0 5px rgba(244, 67, 54, 0.5), 0 0 10px rgba(244, 67, 54, 0.3); }
    50% { box-shadow: 0 0 20px rgba(244, 67, 54, 0.8), 0 0 40px rgba(244, 67, 54, 0.4); }
    100% { box-shadow: 0 0 5px rgba(244, 67, 54, 0.5), 0 0 10px rgba(244, 67, 54, 0.3); }
  }
  @keyframes ripple {
    0% { transform: scale(0.8); opacity: 1; }
    100% { transform: scale(2.5); opacity: 0; }
  }
  @keyframes slideIn {
    0% { transform: translateX(-20px); opacity: 0; }
    100% { transform: translateX(0); opacity: 1; }
  }
  @keyframes urgentBorder {
    0% { border-color: rgba(244, 67, 54, 0.5); }
    50% { border-color: rgba(244, 67, 54, 1); }
    100% { border-color: rgba(244, 67, 54, 0.5); }
  }
`;

type AreaAlertBounds = { north: number; south: number; east: number; west: number };
type AreaAlertsViewport = {
  bounds: AreaAlertBounds;
  zoom: number;
};
type AlertSubTab = 'sos' | 'traffic' | 'weather' | 'missions';

interface AlertsTabProps {
  selectedCourse?: string;
  selectedDeviceType?: string;
  selectedEntityType?: string;
  selectedEntity?: string;
  onSOSClick?: (message: SOSMessage) => void;
  trafficIncidents?: TrafficIncident[];
  weatherAlerts?: WeatherAlert[];
  areaAlertsLoading?: boolean;
  areaAlertsError?: string | null;
  areaAlertsLoadedAt?: string | null;
  areaAlertsStale?: boolean;
  areaAlertsViewport?: AreaAlertsViewport | null;
  onLoadAreaAlerts?: (payload: {
    bounds: AreaAlertBounds;
    zoom: number;
    force?: boolean;
  }) => void;
  onAreaAlertClick?: (alert: TrafficIncident | WeatherAlert) => void;
  /** Events raised against missions under way. Empty when the module is off. */
  missionEvents?: MissionEvent[];
  missionRoutes?: MissionRoute[];
  missionsEnabled?: boolean;
  onMissionEventClick?: (event: MissionEvent) => void;
  onAcknowledgeMissionEvent?: (event: MissionEvent) => void;
  onPromoteMissionThreat?: (event: MissionEvent) => void;
}

const AlertsTab: React.FC<AlertsTabProps> = ({
  selectedCourse,
  selectedDeviceType,
  selectedEntityType,
  selectedEntity,
  onSOSClick,
  trafficIncidents = [] as TrafficIncident[],
  weatherAlerts = [] as WeatherAlert[],
  areaAlertsLoading = false,
  areaAlertsError = null,
  areaAlertsLoadedAt = null,
  areaAlertsStale = false,
  areaAlertsViewport = null as AreaAlertsViewport | null,
  onLoadAreaAlerts,
  onAreaAlertClick,
  missionEvents = [] as MissionEvent[],
  missionRoutes = [] as MissionRoute[],
  missionsEnabled = false,
  onMissionEventClick,
  onAcknowledgeMissionEvent,
  onPromoteMissionThreat
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  // Theme-aware colors - Dark slate
  const colors = useMemo(() => ({
    background: isDark 
      ? 'linear-gradient(180deg, rgba(30, 32, 40, 1) 0%, rgba(24, 26, 32, 1) 100%)'
      : 'linear-gradient(180deg, #ffffff 0%, #f5f7fa 100%)',
    text: isDark ? 'white' : '#1a1a2e',
    textSecondary: isDark ? '#888' : '#666',
    textTertiary: isDark ? '#666' : '#999',
    cardBg: isDark ? 'rgba(45, 45, 60, 0.8)' : 'rgba(255, 255, 255, 0.95)',
    cardBgUrgent: isDark 
      ? 'linear-gradient(135deg, rgba(244, 67, 54, 0.15), rgba(30, 30, 50, 0.95))'
      : 'linear-gradient(135deg, rgba(244, 67, 54, 0.1), rgba(255, 255, 255, 0.98))',
    messageBg: isDark ? 'rgba(10, 10, 20, 0.8)' : 'rgba(244, 67, 54, 0.05)',
    inputBg: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
    inputBorder: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.15)',
  }), [isDark]);

  const [sosMessages, setSOSMessages] = useState<SOSMessage[]>([]);
  const [filteredMessages, setFilteredMessages] = useState<SOSMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeAlertSubTab, setActiveAlertSubTab] = useState<AlertSubTab>('sos');

  const loadSOSMessages = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await copAlertsService.getSOSMessages(selectedCourse, 720, 200);
      setSOSMessages(response.messages);
    } catch (err: any) {
      console.error('[Alerts Tab] Error loading SOS messages:', err);
      
      if (err?.message?.includes('500')) {
        setError('SOS Alerts endpoint not available. Please restart the backend to enable this feature.');
      } else {
        setError('Failed to load SOS messages. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedCourse]);

  const formatTimestamp = (message: SOSMessage): string => {
    try {
      const date = new Date(message.sms_timestamp);
      return date.toLocaleString();
    } catch {
      return message.sms_timestamp;
    }
  };

  const getTimeAgo = (message: SOSMessage): string => {
    try {
      const date = new Date(message.sms_timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays}d ago`;
    } catch {
      return '';
    }
  };

  // Check if message is recent (within 30 minutes) for urgent styling
  const isUrgent = (message: SOSMessage): boolean => {
    try {
      const date = new Date(message.sms_timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      return diffMs < 30 * 60 * 1000; // 30 minutes
    } catch {
      return false;
    }
  };

  // Use the shared device color util so SOS chips match the map pin / list
  // / legend for the same device. Student SOSes are colored by their
  // device_type (phone=green / watch=blue / vehicle=purple); RP/SRP keep
  // their role color.
  const getEntityColor = (message: Partial<Pick<SOSMessage, 'entity_type' | 'device_type' | 'entity_id'>>): string =>
    getDeviceColor(message);

  const getSenderName = (message: SOSMessage): string => {
    if (message.entity_id) {
      return message.entity_id;
    }
    
    if (message.deviceid) {
      if (message.deviceid.includes('-') && message.deviceid.length > 20) {
        return `Device ${message.deviceid.substring(0, 8)}...`;
      }
      return message.deviceid;
    }
    
    return 'Unknown Device';
  };

  const getFullMessage = (msg: string): string => {
    return msg.trim();
  };

  useEffect(() => {
    loadSOSMessages();
  }, [loadSOSMessages]);

  useEffect(() => {
    let filtered = sosMessages;
    
    if (selectedDeviceType && selectedDeviceType !== 'All') {
      filtered = filtered.filter(msg => 
        msg.device_type?.toLowerCase() === selectedDeviceType.toLowerCase()
      );
    }
    
    if (selectedEntityType && selectedEntityType !== 'All') {
      filtered = filtered.filter(msg => msg.entity_type === selectedEntityType);
    }
    
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(msg => 
        msg.deviceid.toLowerCase().includes(term) ||
        (msg.entity_id && msg.entity_id.toLowerCase().includes(term)) ||
        (msg.sms_message && msg.sms_message.toLowerCase().includes(term)) ||
        (msg.sms_number && msg.sms_number.toLowerCase().includes(term))
      );
    }
    
    if (selectedEntity && selectedEntity !== 'All') {
      filtered = filtered.filter(msg => msg.entity_id === selectedEntity);
    }
    
    setFilteredMessages(filtered);
  }, [searchTerm, sosMessages, selectedDeviceType, selectedEntityType, selectedEntity]);

  // Count urgent and acknowledged messages
  const trafficMatches = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return trafficIncidents;
    return trafficIncidents.filter((incident) => [
      incident.title,
      incident.description,
      incident.type,
      incident.severity,
      incident.road,
      incident.source,
    ].some((value) => (value || '').toLowerCase().includes(term)));
  }, [searchTerm, trafficIncidents]);

  const weatherMatches = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return weatherAlerts;
    return weatherAlerts.filter((alert) => [
      alert.event,
      alert.headline,
      alert.description,
      alert.severity,
      alert.area_description,
      alert.source,
    ].some((value) => (value || '').toLowerCase().includes(term)));
  }, [searchTerm, weatherAlerts]);

  const missionNames = useMemo(() => {
    const names = new Map<number, string>();
    missionRoutes.forEach((route) => names.set(route.mission_id, route.name));
    return names;
  }, [missionRoutes]);

  const missionMatches = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const sorted = [...missionEvents].sort(
      (a, b) => new Date(b.detected_at).getTime() - new Date(a.detected_at).getTime(),
    );
    if (!term) return sorted;
    return sorted.filter((event) => [
      event.label,
      event.device_id,
      event.event_type,
      event.severity,
      missionNames.get(event.mission_id),
    ].some((value) => (value || '').toLowerCase().includes(term)));
  }, [searchTerm, missionEvents, missionNames]);

  const zoom = areaAlertsViewport?.zoom ?? 0;
  const showGroupedAreaAlerts = zoom > 0 && zoom < 8;
  const showRegionalPrompt = zoom > 0 && zoom < 8;

  const trafficGroups = useMemo(() => {
    const groups = new Map<string, number>();
    trafficMatches.forEach((incident) => {
      const key = `${incident.type.replace('_', ' ')} / ${incident.severity}`;
      groups.set(key, (groups.get(key) || 0) + 1);
    });
    return Array.from(groups.entries()).sort((a, b) => b[1] - a[1]);
  }, [trafficMatches]);

  const weatherGroups = useMemo(() => {
    const groups = new Map<string, number>();
    weatherMatches.forEach((alert) => {
      const key = `${alert.event} / ${alert.severity}`;
      groups.set(key, (groups.get(key) || 0) + 1);
    });
    return Array.from(groups.entries()).sort((a, b) => b[1] - a[1]);
  }, [weatherMatches]);

  const areaUrgentCount = trafficMatches.filter((incident) => ['major', 'severe'].includes(incident.severity)).length +
    weatherMatches.filter((alert) => ['severe', 'extreme'].includes(alert.severity)).length;
  const urgentCount = filteredMessages.filter(msg => isUrgent(msg) && !msg.acknowledged).length + areaUrgentCount;
  const acknowledgedCount = filteredMessages.filter(msg => msg.acknowledged).length;
  const sosUrgentCount = filteredMessages.filter(msg => isUrgent(msg) && !msg.acknowledged).length;
  const activeAreaCount = activeAlertSubTab === 'traffic' ? trafficMatches.length : weatherMatches.length;
  const activeAreaTitle = activeAlertSubTab === 'traffic' ? 'Traffic Incidents' : 'Weather Alerts';
  const activeAreaAccent = activeAlertSubTab === 'traffic' ? '#ff9800' : '#03a9f4';
  const activeAreaIcon = activeAlertSubTab === 'traffic'
    ? <TrafficIcon sx={{ color: activeAreaAccent, fontSize: 20 }} />
    : <WeatherIcon sx={{ color: activeAreaAccent, fontSize: 20 }} />;
  const activeAreaEmptyLabel = activeAlertSubTab === 'traffic'
    ? 'No traffic incidents loaded, click refresh to load'
    : 'No weather alerts loaded, click refresh to load';
  const activeAreaEmptyIcon = activeAlertSubTab === 'traffic'
    ? <TrafficIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
    : <WeatherIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />;

  const handleAreaAlertsRefresh = () => {
    if (!areaAlertsViewport || !onLoadAreaAlerts) return;
    onLoadAreaAlerts({
      bounds: areaAlertsViewport.bounds,
      zoom: areaAlertsViewport.zoom,
      force: true,
    });
  };
  const topRefreshDisabled = activeAlertSubTab !== 'sos' && (!areaAlertsViewport || !onLoadAreaAlerts || areaAlertsLoading);
  const handleTopRefresh = () => {
    if (activeAlertSubTab === 'sos') {
      loadSOSMessages();
      return;
    }
    handleAreaAlertsRefresh();
  };

  const renderDetailLine = (label: string, value?: React.ReactNode) => {
    if (value === undefined || value === null || value === '') return null;
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: '88px 1fr', gap: 1, py: 0.25 }}>
        <Typography variant="caption" sx={{ color: colors.textTertiary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ color: colors.textSecondary }}>
          {value}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      background: colors.background
    }}>
      {/* Inject keyframe animations */}
      <style>{pulseAnimation}</style>
      
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        borderBottom: '1px solid rgba(244, 67, 54, 0.3)',
        background: urgentCount > 0 
          ? 'linear-gradient(90deg, rgba(244, 67, 54, 0.1), transparent)'
          : 'transparent'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Animated Alert Icon */}
            <Box sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {urgentCount > 0 && (
                <>
                  {/* Ripple effect */}
                  <Box sx={{
                    position: 'absolute',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    border: '2px solid #f44336',
                    animation: 'ripple 1.5s infinite',
                  }} />
                  <Box sx={{
                    position: 'absolute',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    border: '2px solid #f44336',
                    animation: 'ripple 1.5s infinite 0.5s',
                  }} />
                </>
              )}
              <Badge 
                badgeContent={urgentCount} 
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    animation: urgentCount > 0 ? 'pulse 1s infinite' : 'none',
                    fontWeight: 'bold',
                  }
                }}
              >
                <WarningIcon sx={{ 
                  color: '#f44336', 
                  fontSize: 32,
                  filter: urgentCount > 0 ? 'drop-shadow(0 0 8px rgba(244, 67, 54, 0.8))' : 'none',
                  animation: urgentCount > 0 ? 'pulse 2s infinite' : 'none'
                }} />
              </Badge>
            </Box>
            
            <Box>
              <Typography variant="h6" sx={{ 
                color: colors.text, 
                fontWeight: 'bold',
                textShadow: urgentCount > 0 && isDark ? '0 0 10px rgba(244, 67, 54, 0.5)' : 'none'
              }}>
                Alerts
              </Typography>
              <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                SOS {filteredMessages.length} • Traffic {trafficMatches.length} • Weather {weatherMatches.length} • {urgentCount} urgent{acknowledgedCount > 0 ? ` • ${acknowledgedCount} acknowledged` : ''}
              </Typography>
            </Box>
          </Box>
          
          <IconButton 
            onClick={handleTopRefresh}
            disabled={topRefreshDisabled}
            size="small" 
            sx={{ 
              color: theme.palette.primary.main,
              '&:hover': {
                background: `rgba(${(() => {
                  const hex = theme.palette.primary.main;
                  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                  return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '76, 175, 80';
                })()}, 0.1)`,
              }
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>

        {/* Search Box */}
        <TextField
          size="small"
          placeholder="Search SOS, traffic, weather, road, entity, or message..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.textTertiary }} />
              </InputAdornment>
            ),
            sx: {
              color: colors.text,
              bgcolor: colors.inputBg,
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.inputBorder
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark ? `${theme.palette.primary.main}50` : 'rgba(244, 67, 54, 0.3)'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark ? theme.palette.primary.main : '#f44336'
              }
            }
          }}
        />

        <Paper
          elevation={0}
          sx={{
            mt: 2,
            p: 0.5,
            borderRadius: 2,
            bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
            border: `1px solid ${colors.inputBorder}`,
          }}
        >
          <Tabs
            value={activeAlertSubTab}
            onChange={(_event, value: AlertSubTab) => setActiveAlertSubTab(value)}
            variant="fullWidth"
            sx={{
              minHeight: 36,
              '& .MuiTab-root': {
                minHeight: 36,
                py: 0.75,
                borderRadius: 1.5,
                color: colors.textSecondary,
                fontSize: '0.72rem',
                fontWeight: 800,
                letterSpacing: '0.03em',
              },
              '& .Mui-selected': {
                color: `${theme.palette.primary.main} !important`,
                bgcolor: isDark ? 'rgba(76, 175, 80, 0.14)' : 'rgba(76, 175, 80, 0.10)',
              },
              '& .MuiTabs-indicator': {
                display: 'none',
              },
            }}
          >
            <Tab
              value="sos"
              icon={<WarningIcon fontSize="small" />}
              iconPosition="start"
              label={`SOS (${filteredMessages.length})`}
            />
            <Tab
              value="traffic"
              icon={<TrafficIcon fontSize="small" />}
              iconPosition="start"
              label={`Traffic (${trafficMatches.length})`}
            />
            <Tab
              value="weather"
              icon={<WeatherIcon fontSize="small" />}
              iconPosition="start"
              label={`Weather (${weatherMatches.length})`}
            />
            {missionsEnabled && (
              <Tab
                value="missions"
                icon={<MissionIcon fontSize="small" />}
                iconPosition="start"
                label={`Missions (${missionMatches.length})`}
              />
            )}
          </Tabs>
        </Paper>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
          <CircularProgress sx={{ color: '#f44336' }} />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Box sx={{ p: 2 }}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Box>
      )}

      {/* SOS Messages List */}
      {!loading && !error && (
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '3px',
          },
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255, 255, 255, 0.3) transparent',
        }}>
          {(activeAlertSubTab === 'traffic' || activeAlertSubTab === 'weather') && (
          <Box sx={{ p: 2, pb: 0 }}>
            {areaAlertsLoading && !areaAlertsLoadedAt ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4, pb: 3 }}>
                <CircularProgress size={30} sx={{ color: activeAreaAccent }} />
              </Box>
            ) : !areaAlertsLoadedAt && !areaAlertsError ? (
              <Box sx={{ textAlign: 'center', pt: 4, pb: 3, opacity: 0.7 }}>
                {activeAreaEmptyIcon}
                <Typography variant="body2" sx={{ opacity: 0.5 }}>
                  {activeAreaEmptyLabel}
                </Typography>
              </Box>
            ) : (
            <Paper
              sx={{
                p: 1.5,
                mb: 2,
                background: colors.cardBg,
                border: `1px solid ${activeAreaAccent}55`,
                boxShadow: isDark ? `0 0 22px ${activeAreaAccent}11` : '0 8px 24px rgba(0,0,0,0.08)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {activeAreaIcon}
                  <Typography variant="subtitle2" sx={{ color: colors.text, fontWeight: 'bold' }}>
                    {activeAreaTitle}
                  </Typography>
                  <Chip label={`${activeAreaCount} loaded`} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: `${activeAreaAccent}22`, color: activeAreaAccent }} />
                  {areaAlertsStale && (
                    <Chip label="stale view" size="small" color="warning" sx={{ height: 20, fontSize: '0.65rem' }} />
                  )}
                </Box>
              </Box>

              {areaAlertsError && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  {areaAlertsError}
                </Alert>
              )}

              {showRegionalPrompt && areaAlertsLoadedAt && (
                <Typography variant="caption" sx={{ color: colors.textTertiary, display: 'block', mb: 1 }}>
                  Zoom in for detailed alerts.
                </Typography>
              )}

              {areaAlertsLoadedAt && (
                <Typography variant="caption" sx={{ color: colors.textTertiary, display: 'block', mb: 1 }}>
                  Loaded {new Date(areaAlertsLoadedAt).toLocaleString()}
                  {areaAlertsViewport ? ` • zoom ${areaAlertsViewport.zoom.toFixed(1)}` : ''}
                </Typography>
              )}

              {showGroupedAreaAlerts ? (
                <Box sx={{ display: 'grid', gap: 1 }}>
                  {(activeAlertSubTab === 'traffic'
                    ? trafficGroups.map(([label, count]) => ({ label, count, color: '#ff9800', icon: 'traffic' }))
                    : weatherGroups.map(([label, count]) => ({ label, count, color: '#03a9f4', icon: 'weather' }))
                  ).slice(0, 8).map((group) => (
                    <Box key={`${group.icon}-${group.label}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, borderRadius: 1, bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {group.icon === 'traffic' ? <TrafficIcon sx={{ color: group.color, fontSize: 16 }} /> : <WeatherIcon sx={{ color: group.color, fontSize: 16 }} />}
                        <Typography variant="caption" sx={{ color: colors.textSecondary, textTransform: 'capitalize' }}>
                          {group.label}
                        </Typography>
                      </Box>
                      <Chip label={group.count} size="small" sx={{ height: 20, bgcolor: `${group.color}22`, color: group.color }} />
                    </Box>
                  ))}
                  {((activeAlertSubTab === 'traffic' && trafficGroups.length === 0) || (activeAlertSubTab === 'weather' && weatherGroups.length === 0)) && areaAlertsLoadedAt && (
                    <Typography variant="caption" sx={{ color: colors.textTertiary }}>
                      No {activeAlertSubTab} alerts in the loaded view.
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: 'grid', gap: 1 }}>
                  {activeAlertSubTab === 'traffic' && trafficMatches.slice(0, 75).map((incident) => (
                    <Paper
                      key={`traffic-${incident.id}`}
                      onClick={() => onAreaAlertClick?.(incident)}
                      sx={{
                        p: 1,
                        cursor: onAreaAlertClick ? 'pointer' : 'default',
                        bgcolor: isDark ? 'rgba(255, 152, 0, 0.08)' : 'rgba(255, 152, 0, 0.06)',
                        border: '1px solid rgba(255, 152, 0, 0.25)',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <TrafficIcon sx={{ color: '#ff9800', fontSize: 16 }} />
                        <Typography variant="caption" sx={{ color: colors.text, fontWeight: 'bold' }}>
                          {incident.title}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: colors.textSecondary, display: 'block' }}>
                        {incident.description}
                      </Typography>
                      <Box sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: isDark ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.65)' }}>
                        {renderDetailLine('Type', incident.type.replace('_', ' '))}
                        {renderDetailLine('Severity', incident.severity)}
                        {renderDetailLine('Road', incident.road)}
                        {renderDetailLine('Segment', incident.from_location && incident.to_location ? `${incident.from_location} to ${incident.to_location}` : undefined)}
                        {renderDetailLine('Length', typeof incident.length_meters === 'number' ? `${(incident.length_meters / 1609.34).toFixed(1)} mi (${Math.round(incident.length_meters)} m)` : undefined)}
                        {renderDetailLine('Delay', typeof incident.delay_seconds === 'number' && incident.delay_seconds > 0 ? `${Math.round(incident.delay_seconds / 60)} min (${incident.delay_seconds}s)` : undefined)}
                        {renderDetailLine('Validity', incident.time_validity)}
                        {renderDetailLine('Confidence', incident.probability_of_occurrence)}
                        {renderDetailLine('Reports', typeof incident.number_of_reports === 'number' ? incident.number_of_reports : undefined)}
                        {renderDetailLine('Start', incident.start_time ? new Date(incident.start_time).toLocaleString() : undefined)}
                        {renderDetailLine('End', incident.end_time ? new Date(incident.end_time).toLocaleString() : undefined)}
                        {renderDetailLine('Event Code', incident.event_code)}
                      </Box>
                    </Paper>
                  ))}

                  {activeAlertSubTab === 'weather' && weatherMatches.slice(0, 50).map((alert) => (
                    <Paper
                      key={`weather-${alert.id}`}
                      onClick={() => onAreaAlertClick?.(alert)}
                      sx={{
                        p: 1,
                        cursor: onAreaAlertClick && alert.latitude != null && alert.longitude != null ? 'pointer' : 'default',
                        bgcolor: isDark ? 'rgba(3, 169, 244, 0.08)' : 'rgba(3, 169, 244, 0.06)',
                        border: '1px solid rgba(3, 169, 244, 0.25)',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <WeatherIcon sx={{ color: '#03a9f4', fontSize: 16 }} />
                        <Typography variant="caption" sx={{ color: colors.text, fontWeight: 'bold' }}>
                          {alert.headline}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: colors.textSecondary, display: 'block' }}>
                        {alert.description}
                      </Typography>
                      <Box sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: isDark ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.65)' }}>
                        {renderDetailLine('Event', alert.event)}
                        {renderDetailLine('Severity', alert.severity)}
                        {renderDetailLine('Urgency', alert.urgency)}
                        {renderDetailLine('Certainty', alert.certainty)}
                        {renderDetailLine('Area', alert.area_description)}
                        {renderDetailLine('Effective', alert.effective ? new Date(alert.effective).toLocaleString() : undefined)}
                        {renderDetailLine('Expires', alert.expires ? new Date(alert.expires).toLocaleString() : undefined)}
                        {renderDetailLine('Instruction', alert.instruction)}
                      </Box>
                    </Paper>
                  ))}

                  {((activeAlertSubTab === 'traffic' && trafficMatches.length === 0) || (activeAlertSubTab === 'weather' && weatherMatches.length === 0)) && areaAlertsLoadedAt && (
                    <Typography variant="caption" sx={{ color: colors.textTertiary }}>
                      No {activeAlertSubTab} alerts in the loaded view.
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>
            )}

            {(areaAlertsLoadedAt || areaAlertsError) && (
              <Divider sx={{ borderColor: 'rgba(244, 67, 54, 0.2)', mb: 2 }} />
            )}
          </Box>
          )}

          {activeAlertSubTab === 'missions' && (
            <Box sx={{ p: 2 }}>
              {missionRoutes.length === 0 ? (
                <Box sx={{ textAlign: 'center', pt: 4, pb: 3, opacity: 0.7 }}>
                  <MissionIcon sx={{ fontSize: 40, color: colors.textTertiary, mb: 1 }} />
                  <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                    No movement under way.
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.textTertiary }}>
                    Set a mission to In Progress to put its route and threats on the map.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                    {missionRoutes.map((route) => (
                      <Chip
                        key={route.mission_id}
                        size="small"
                        icon={<MissionIcon sx={{ fontSize: 14 }} />}
                        label={route.name}
                        sx={{
                          height: 22,
                          fontSize: '0.68rem',
                          bgcolor: 'rgba(41, 182, 246, 0.16)',
                          color: '#29b6f6',
                        }}
                      />
                    ))}
                  </Box>

                  {missionMatches.length === 0 ? (
                    <Typography variant="caption" sx={{ color: colors.textTertiary }}>
                      {searchTerm ? 'No mission events match your search.' : 'Nothing raised yet on this movement.'}
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'grid', gap: 1 }}>
                      {missionMatches.slice(0, 100).map((event) => {
                        const accent = missionSeverityColor(event.severity);
                        const canLocate =
                          typeof event.latitude === 'number' && typeof event.longitude === 'number';
                        const threat = isThreatEvent(event);
                        const identifier =
                          (event.meta?.external_id as string | undefined) ||
                          (event.meta?.identifier as string | undefined);
                        return (
                          <Paper
                            key={`mission-event-${event.id}`}
                            onClick={() => canLocate && onMissionEventClick?.(event)}
                            sx={{
                              p: 1,
                              cursor: canLocate && onMissionEventClick ? 'pointer' : 'default',
                              bgcolor: isDark ? `${accent}14` : `${accent}0d`,
                              border: `1px solid ${accent}40`,
                              opacity: event.acknowledged_at ? 0.55 : 1,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <MissionIcon sx={{ color: accent, fontSize: 16 }} />
                              <Typography variant="caption" sx={{ color: colors.text, fontWeight: 'bold', flex: 1 }}>
                                {missionEventLabel(event.event_type)}
                              </Typography>
                              {!event.acknowledged_at && onAcknowledgeMissionEvent && (
                                <IconButton
                                  size="small"
                                  title="Acknowledge"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onAcknowledgeMissionEvent(event);
                                  }}
                                  sx={{ p: 0.25, color: colors.textSecondary }}
                                >
                                  <AckIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              )}
                              {threat && identifier && onPromoteMissionThreat && (
                                <IconButton
                                  size="small"
                                  title="Add this emitter to the watchlist"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onPromoteMissionThreat(event);
                                  }}
                                  sx={{ p: 0.25, color: accent }}
                                >
                                  <PromoteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              )}
                            </Box>
                            <Typography variant="caption" sx={{ color: colors.textSecondary, display: 'block' }}>
                              {missionNames.get(event.mission_id) || `Mission ${event.mission_id}`}
                              {event.label ? ` • ${event.label}` : ''}
                            </Typography>
                            <Typography variant="caption" sx={{ color: colors.textTertiary, display: 'block' }}>
                              {new Date(event.detected_at).toLocaleTimeString()}
                              {typeof event.distance_m === 'number'
                                ? ` • ${Math.round(event.distance_m)} m from route`
                                : ''}
                              {typeof event.segment_index === 'number'
                                ? ` • segment ${event.segment_index + 1}`
                                : ''}
                            </Typography>
                          </Paper>
                        );
                      })}
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}

          {activeAlertSubTab === 'sos' && (filteredMessages.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <AlertActiveIcon sx={{ fontSize: 48, color: colors.textTertiary, mb: 2 }} />
              <Typography sx={{ color: colors.textSecondary }}>
                {searchTerm ? 'No SOS messages match your search' : 'No SOS messages found'}
              </Typography>
              {!searchTerm && (
                <Typography sx={{ color: colors.textTertiary, mt: 1, fontSize: '0.875rem' }}>
                  SOS messages will appear here when devices send emergency alerts
                </Typography>
              )}
            </Box>
          ) : (
            <List sx={{ p: 2 }}>
              {filteredMessages.map((message, index) => {
                const urgent = isUrgent(message) && !message.acknowledged;
                const acknowledged = message.acknowledged;
                
                return (
                  <Paper
                    key={message.id}
                    sx={{
                      mb: 2,
                      background: acknowledged 
                        ? (isDark ? 'rgba(30, 32, 40, 0.6)' : 'rgba(240, 240, 240, 0.8)')
                        : (urgent ? colors.cardBgUrgent : colors.cardBg),
                      border: acknowledged 
                        ? '1px solid rgba(100, 100, 100, 0.3)'
                        : (urgent ? '2px solid' : '1px solid rgba(244, 67, 54, 0.3)'),
                      borderColor: acknowledged 
                        ? 'rgba(100, 100, 100, 0.3)'
                        : (urgent ? 'rgba(244, 67, 54, 0.8)' : 'rgba(244, 67, 54, 0.3)'),
                      animation: urgent 
                        ? 'slideIn 0.3s ease, urgentBorder 2s infinite, glow 2s infinite' 
                        : `slideIn 0.3s ease ${index * 0.05}s`,
                      cursor: onSOSClick ? 'pointer' : 'default',
                      transition: 'all 0.3s ease',
                      backdropFilter: 'blur(5px)',
                      boxShadow: isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.06)',
                      opacity: acknowledged ? 0.7 : 1,
                      '&:hover': onSOSClick ? {
                        transform: 'translateX(4px)',
                        borderColor: acknowledged ? 'rgba(100, 100, 100, 0.5)' : '#ff5252',
                        boxShadow: acknowledged ? '0 4px 15px rgba(0,0,0,0.15)' : '0 4px 20px rgba(244, 67, 54, 0.3)'
                      } : {}
                    }}
                    onClick={() => onSOSClick && onSOSClick(message)}
                  >
                    <Box sx={{ p: 2 }}>
                      {/* Header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ position: 'relative' }}>
                            <WarningIcon sx={{ 
                              color: acknowledged ? '#888' : '#f44336', 
                              fontSize: 24,
                              animation: urgent ? 'pulse 1s infinite' : 'none',
                              filter: urgent ? 'drop-shadow(0 0 4px rgba(244, 67, 54, 0.8))' : 'none'
                            }} />
                            {urgent && (
                              <Box sx={{
                                position: 'absolute',
                                top: -2,
                                right: -2,
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: '#f44336',
                                animation: 'pulse 1s infinite',
                              }} />
                            )}
                          </Box>
                          
                          <Typography variant="subtitle1" sx={{ 
                            color: acknowledged ? colors.textSecondary : colors.text, 
                            fontWeight: 'bold',
                            textShadow: urgent && isDark ? '0 0 8px rgba(244, 67, 54, 0.5)' : 'none'
                          }}>
                            {getSenderName(message)}
                          </Typography>
                          {message.entity_type && (
                            <Chip
                              label={message.entity_type}
                              size="small"
                              sx={{
                                bgcolor: acknowledged ? 'rgba(100, 100, 100, 0.5)' : getEntityColor(message),
                                color: 'white',
                                fontSize: '0.7rem',
                                height: '20px',
                                fontWeight: 'bold',
                                boxShadow: acknowledged ? 'none' : `0 0 8px ${getEntityColor(message)}40`
                              }}
                            />
                          )}
                          {acknowledged && (
                            <Chip
                              label="ACKNOWLEDGED"
                              size="small"
                              sx={{
                                bgcolor: 'rgba(76, 175, 80, 0.2)',
                                color: '#4caf50',
                                fontSize: '0.6rem',
                                height: '18px',
                                fontWeight: 'bold',
                                border: '1px solid rgba(76, 175, 80, 0.4)'
                              }}
                            />
                          )}
                          {urgent && (
                            <Chip
                              label="URGENT"
                              size="small"
                              sx={{
                                bgcolor: 'rgba(244, 67, 54, 0.3)',
                                color: '#ff5252',
                                fontSize: '0.6rem',
                                height: '18px',
                                fontWeight: 'bold',
                                animation: 'pulse 1.5s infinite',
                                border: '1px solid rgba(244, 67, 54, 0.5)'
                              }}
                            />
                          )}
                        </Box>
                        <Chip
                          label={getTimeAgo(message)}
                          size="small"
                          sx={{
                            bgcolor: urgent ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.15)',
                            color: urgent ? '#ff5252' : '#f44336',
                            fontSize: '0.7rem',
                            fontWeight: 'bold'
                          }}
                        />
                      </Box>

                      {/* Message Content */}
                      <Box sx={{ 
                        bgcolor: acknowledged 
                          ? (isDark ? 'rgba(50, 50, 60, 0.5)' : 'rgba(220, 220, 220, 0.5)')
                          : colors.messageBg, 
                        p: 1.5, 
                        borderRadius: 1.5,
                        border: acknowledged 
                          ? '1px solid rgba(100, 100, 100, 0.3)'
                          : '1px solid rgba(244, 67, 54, 0.3)',
                        mb: 1.5
                      }}>
                        <Typography variant="body1" sx={{ 
                          color: acknowledged ? colors.textSecondary : (urgent ? '#ff5252' : '#f44336'), 
                          fontWeight: 'bold', 
                          fontSize: '1rem', 
                          whiteSpace: 'pre-wrap',
                          textShadow: urgent && isDark ? '0 0 10px rgba(244, 67, 54, 0.5)' : 'none'
                        }}>
                          {getFullMessage(message.sms_message)}
                        </Typography>
                      </Box>

                      {/* Details */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {message.sms_number && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon sx={{ color: colors.textTertiary, fontSize: 14 }} />
                            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                              {message.sms_number}
                            </Typography>
                          </Box>
                        )}
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TimeIcon sx={{ color: colors.textTertiary, fontSize: 14 }} />
                          <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                            {formatTimestamp(message)}
                          </Typography>
                        </Box>

                        {message.latitude && message.longitude && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationIcon sx={{ color: colors.textTertiary, fontSize: 14 }} />
                            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                              {message.latitude.toFixed(4)}, {message.longitude.toFixed(4)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      
                      {/* Acknowledged info */}
                      {acknowledged && message.acknowledged_by && (
                        <Box sx={{ 
                          mt: 1.5, 
                          pt: 1.5, 
                          borderTop: '1px solid rgba(100, 100, 100, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}>
                          <Typography variant="caption" sx={{ color: '#4caf50' }}>
                            ✓ Acknowledged by {message.acknowledged_by}
                            {message.acknowledged_at && ` at ${new Date(message.acknowledged_at).toLocaleString()}`}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                );
              })}
            </List>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AlertsTab;
